import type { NexoyaTeam } from '../types/types';

/**
 * The tenant axis, shaped exactly like the plan axis in `./plans.ts`: three states, not a
 * boolean. `null` is "not known yet" and is NEVER a brand — collapsing it into `whiteLabel`
 * hides the vendor from its own customers, collapsing it into `nexoya` puts the vendor's name
 * in front of an agency's client. Both are wrong; render neither variant instead.
 */
export const NEXOYA_TENANT_ID = 1;

export type TenantBrand = 'nexoya' | 'whiteLabel';

/** `Partial`: a payload carries only what its document selected, whatever Apollo's type says. */
type TeamTenantPayload = Partial<NexoyaTeam> | null | undefined;

/**
 * Resolves the tenant brand for `teamId`, or `null` if it cannot be trusted yet.
 *
 * The `team_id` check is the team-switch guard, and it is the same one `planForTeam` carries and
 * for the same reason: Apollo keeps the previous team's `data` on screen while refetching, so
 * without it a switch from a Nexoya team to a white-label one renders our wordmark, our green
 * demo CTA and our plan name to somebody else's client.
 *
 * `organization` absent from the payload means the document did not select it (`withOrg: false`)
 * — unknown, not white-label. Present but null, or a tenant that is not ours, is an ANSWER:
 * white-label. Only a payload we do trust resolves to a brand.
 */
export function tenantBrandForTeam(teamId: number, team: TeamTenantPayload): TenantBrand | null {
  if (!team || team.team_id !== teamId) return null;
  if (!('organization' in team)) return null;
  return team.organization?.tenant?.tenant_id === NEXOYA_TENANT_ID ? 'nexoya' : 'whiteLabel';
}
